import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Upload, X, Loader2, Image as ImageIcon, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '../../api/axios';

/**
 * BulkImageUploadDialog — Fügt mehrere Bilder auf einmal zu einem bestehenden
 * Live-Produkt hinzu. Drag & Drop unterstützt, mit Vorschau und Upload-Progress.
 *
 * Verwendet den Backend-Endpoint POST /api/products/{id}/append-images.
 */
export default function BulkImageUploadDialog({ isOpen, onClose, product, onUploadComplete }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setFiles([]);
    setUploading(false);
    setDragActive(false);
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const addFiles = (fileList) => {
    const valid = Array.from(fileList).filter(f => {
      if (!f.type.startsWith('image/')) {
        toast.error(`${f.name}: kein Bild`);
        return false;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name}: größer als 10 MB`);
        return false;
      }
      return true;
    });
    setFiles(prev => [
      ...prev,
      ...valid.map(f => ({
        file: f,
        name: f.name,
        size: f.size,
        preview: URL.createObjectURL(f),
      })),
    ]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  const removeFile = (idx) => {
    setFiles(prev => {
      const rem = prev.filter((_, i) => i !== idx);
      URL.revokeObjectURL(prev[idx].preview);
      return rem;
    });
  };

  const upload = async () => {
    if (!files.length || !product?.id) return;

    setUploading(true);
    const formData = new FormData();
    files.forEach(f => formData.append('files', f.file));

    try {
      const res = await axiosInstance.post(
        `/products/${product.id}/append-images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 }
      );
      const { added, total_images, skipped } = res.data;
      if (skipped?.length) {
        toast.warning(`${added} hochgeladen, ${skipped.length} übersprungen`, {
          description: skipped.slice(0, 3).join('; '),
        });
      } else {
        toast.success(`${added} Bild${added === 1 ? '' : 'er'} hinzugefügt (jetzt ${total_images} insgesamt)`);
      }
      onUploadComplete?.();
      handleClose();
    } catch (e) {
      toast.error('Upload fehlgeschlagen: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  if (!product) return null;
  const currentImagesCount = (product.images || []).length;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto"
        data-testid="bulk-image-upload-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#5D4E3C] flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Bilder hinzufügen · <span className="font-normal text-sm text-[#8B7355]">{product.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current images summary */}
          <div className="bg-[#FAF5EE] border border-[#D4C4B0] p-2 text-xs text-[#5D4E3C]">
            Aktuell <strong>{currentImagesCount}</strong> Bild{currentImagesCount === 1 ? '' : 'er'} bei diesem Produkt.
            Neue Bilder werden am Ende der Galerie angehängt.
          </div>

          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
              dragActive
                ? 'border-[#8B6F4D] bg-[#FAF5EE]'
                : 'border-[#D4C4B0] hover:bg-[#FAF5EE]/40'
            }`}
            data-testid="bulk-upload-dropzone"
          >
            <Upload className="h-8 w-8 mx-auto text-[#8B6F4D] mb-2" />
            <p className="text-sm text-[#5D4E3C] font-semibold">Bilder hier hineinziehen oder klicken</p>
            <p className="text-xs text-[#9C8C7A] mt-1">JPG, PNG, WEBP · bis 10 MB je Bild · mehrere gleichzeitig OK</p>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              data-testid="bulk-upload-file-input"
            />
          </div>

          {/* Preview grid */}
          {files.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-[#5D4E3C] mb-2">
                {files.length} Bild{files.length === 1 ? '' : 'er'} ausgewählt
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {files.map((f, i) => (
                  <div key={i} className="relative group border border-[#D4C4B0] overflow-hidden" data-testid={`bulk-upload-preview-${i}`}>
                    <img src={f.preview} alt={f.name} className="w-full h-24 object-cover" />
                    <button
                      onClick={() => removeFile(i)}
                      disabled={uploading}
                      className="absolute top-1 right-1 bg-white/90 hover:bg-red-100 border border-[#D4C4B0] p-0.5"
                      title="Entfernen"
                    >
                      <X className="h-3 w-3 text-red-600" />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate">
                      {f.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload button */}
          <Button
            onClick={upload}
            disabled={uploading || !files.length}
            className="w-full rounded-none bg-[#8B6F4D] hover:bg-[#5D4E3C] text-white"
            data-testid="bulk-upload-submit-btn"
          >
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird hochgeladen...</>
            ) : (
              <><CheckCircle2 className="h-4 w-4 mr-2" /> {files.length ? `${files.length} Bild${files.length === 1 ? '' : 'er'} hinzufügen` : 'Bilder auswählen'}</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
